/**
 * Logger - Sistema de logging clínico
 * Registra eventos médicos para auditoría y debugging
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';
type LogCategory = 'system' | 'medical' | 'interaction' | 'physics' | 'diagnostic';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: Record<string, unknown>;
  patientId?: string;
}

class ClinicalLogger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private levelFilter: LogLevel = 'debug';

  // Colores para consola
  private levelColors: Record<LogLevel, string> = {
    debug: '#6B7280',
    info: '#3B82F6',
    warn: '#F59E0B',
    error: '#EF4444',
    critical: '#DC2626',
  };

  private categoryEmojis: Record<LogCategory, string> = {
    system: '⚙️',
    medical: '🏥',
    interaction: '👋',
    physics: '⚛️',
    diagnostic: '🔍',
  };

  /**
   * Registra un mensaje de log
   */
  log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: Record<string, unknown>,
    patientId?: string
  ): void {
    if (this.shouldSkip(level)) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      patientId,
    };

    this.logs.push(entry);

    // Limitar tamaño
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Output a consola
    this.consoleOutput(entry);
  }

  /**
   * Métodos de conveniencia por nivel
   */
  debug(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('debug', category, message, data);
  }

  info(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('info', category, message, data);
  }

  warn(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('warn', category, message, data);
  }

  error(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('error', category, message, data);
  }

  critical(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('critical', category, message, data);
  }

  /**
   * Log específico para eventos médicos
   */
  medical(
    event: string,
    patientId: string,
    details?: Record<string, unknown>
  ): void {
    this.log('info', 'medical', event, details, patientId);
  }

  /**
   * Log de interacción del usuario
   */
  interaction(
    action: string,
    target?: string,
    details?: Record<string, unknown>
  ): void {
    this.log('debug', 'interaction', `${action}${target ? ` -> ${target}` : ''}`, details);
  }

  /**
   * Log de diagnóstico
   */
  diagnostic(
    type: 'pattern_detected' | 'triage_changed' | 'diagnosis_updated',
    patientId: string,
    details: Record<string, unknown>
  ): void {
    const messages: Record<string, string> = {
      pattern_detected: 'Patrón clínico detectado',
      triage_changed: 'Prioridad de triage actualizada',
      diagnosis_updated: 'Diagnóstico actualizado',
    };
    this.log('info', 'diagnostic', messages[type], details, patientId);
  }

  /**
   * Exporta logs a formato JSON
   */
  export(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Filtra logs por criterios
   */
  filter(options: {
    level?: LogLevel;
    category?: LogCategory;
    patientId?: string;
    since?: Date;
    until?: Date;
  }): LogEntry[] {
    return this.logs.filter(log => {
      if (options.level && log.level !== options.level) return false;
      if (options.category && log.category !== options.category) return false;
      if (options.patientId && log.patientId !== options.patientId) return false;
      if (options.since && new Date(log.timestamp) < options.since) return false;
      if (options.until && new Date(log.timestamp) > options.until) return false;
      return true;
    });
  }

  /**
   * Limpia todos los logs
   */
  clear(): void {
    this.logs = [];
  }

  /**
   * Configura el nivel mínimo de log
   */
  setLevel(level: LogLevel): void {
    this.levelFilter = level;
  }

  private shouldSkip(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error', 'critical'];
    return levels.indexOf(level) < levels.indexOf(this.levelFilter);
  }

  private consoleOutput(entry: LogEntry): void {
    const color = this.levelColors[entry.level];
    const emoji = this.categoryEmojis[entry.category];
    const time = new Date(entry.timestamp).toLocaleTimeString();
    
    const prefix = `%c${emoji} [${time}] ${entry.level.toUpperCase()}`;
    const style = `color: ${color}; font-weight: bold`;
    
    if (entry.data) {
      console.log(prefix, style, entry.message, entry.data);
    } else {
      console.log(prefix, style, entry.message);
    }
  }
}

// Exportar singleton
export const logger = new ClinicalLogger();
export default logger;
